import { ComponentChildren } from 'preact';
import type { States, NodeRendererContext, Bounds, DiagramNodeData } from './diagram.types';
import { DiagramNodeElement } from './DiagramNodeElement';
import { layerStyles } from './themes/DiagramStyles.css';

type Props<K1, K2, D1> = {
  nodes: D1[];
  states: States<K1, K2>;
  previousStates: States<K1, K2>;
  nodeRenderer: (context: NodeRendererContext<D1>) => ComponentChildren;
  onNodeSizeChanged: (width: number, height: number, idx: number) => void;
  nodeBounds?: Bounds[];
  supportsSelection: boolean;
};

export function DiagramNodeLayer<K1, K2, D1 extends DiagramNodeData<K1>>({
  nodes,
  states,
  previousStates,
  nodeRenderer,
  nodeBounds,
  onNodeSizeChanged,
  supportsSelection
}: Props<K1, K2, D1>) {
  return (
    <div class={layerStyles}>
      {nodes.map((node, i) => {
        // this is for initial render before the layout function so render all nodes at 0,0
        const position =
          nodeBounds && nodeBounds[i] ? { x: nodeBounds[i].x, y: nodeBounds[i].y } : { x: 0, y: 0 };
        const state = {
          selected: states.selectedNodeIds.includes(node.id),
          focused: states.focusedId === node.id,
          hovered: states.hoveredId === node.id,
          zoom: states.zoom
        };
        const previousState = {
          selected: previousStates.selectedNodeIds.includes(node.id),
          focused: previousStates.focusedId === node.id,
          hovered: previousStates.hoveredId === node.id,
          zoom: previousStates.zoom
        };
        return (
          <DiagramNodeElement
            id={node.id}
            position={position}
            state={state}
            previousState={previousState}
            isDimmed={
              states.highlightedIds.length !== 0 && !states.highlightedIds.includes(node.id)
            }
            activeId={states.activeId}
            nodeRenderer={nodeRenderer}
            onNodeSizeChanged={onNodeSizeChanged}
            nodeIndex={i}
            accessibleLabel={node.accessibleLabel}
            data={node}
            supportsSelection={supportsSelection}
          />
        );
      })}
    </div>
  );
}
